import React, { useState, useMemo } from 'react';
import { Drill } from '@/components/drill-library/types/drill-types';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter } from 'lucide-react';
import DrillCard from '@/components/drill-library/DrillCard';
import { Card, CardContent } from '@/components/ui/card';
import { getDrillDomain, getTagValues } from '@/utils/drill-taxonomy';

interface DrillPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    drills: Drill[];
    onSelect: (drillId: string) => void;
}

const DrillPickerModal: React.FC<DrillPickerModalProps> = ({
    isOpen,
    onClose,
    drills,
    onSelect
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [selectedDomain, setSelectedDomain] = useState<string>('All');
    const [sortBy, setSortBy] = useState<'name' | 'category' | 'recent'>('name');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [selectedDrillId, setSelectedDrillId] = useState<string | null>(null);

    const categories = useMemo(() => {
        const cats = new Set(drills.map(drill => getDrillDomain(drill)).filter(domain => domain && domain !== 'Unknown'));
        return ['All', ...Array.from(cats).sort()];
    }, [drills]);

    const domains = useMemo(() => {
        const doms = new Set(
            drills
                .flatMap(drill => getTagValues(drill, 'Domain'))
                .filter(Boolean)
        );
        return ['All', ...Array.from(doms).sort()];
    }, [drills]);

    const filteredDrills = useMemo(() => {
        let filtered = drills.filter(drill => {
            const domain = getDrillDomain(drill);
            const domainTags = getTagValues(drill, 'Domain');
            const matchesSearch = drill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                domainTags.join(' ').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || domain === selectedCategory;
            const matchesDomain = selectedDomain === 'All' || domainTags.includes(selectedDomain);
            return matchesSearch && matchesCategory && matchesDomain;
        });

        // Sort drills
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'category':
                    return getDrillDomain(a).localeCompare(getDrillDomain(b));
                case 'recent':
                    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
                default:
                    return 0;
            }
        });

        return filtered;
    }, [drills, searchTerm, selectedCategory, selectedDomain, sortBy]);

    const handleSelectDrill = (drillId: string) => {
        setSelectedDrillId(drillId);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Select Drill</DialogTitle>
                    <DialogDescription>
                        Choose a drill to add to your program
                    </DialogDescription>
                </DialogHeader>
                
                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Search and Filter Controls */}
                    <div className="space-y-4 mb-6">
                        {/* Search Bar */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                type="text"
                                placeholder="Search drills by name, category, or domain..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        {/* Basic Filters */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                                <SelectTrigger className="flex-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map(category => (
                                        <SelectItem key={category} value={category}>{category}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            <Button
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <Filter className="h-4 w-4" />
                                Advanced Filters
                            </Button>
                        </div>

                        {/* Advanced Filters */}
                        {showAdvancedFilters && (
                            <Card>
                                <CardContent className="p-4 space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Domain</label>
                                            <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {domains.map(domain => (
                                                        <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Sort By</label>
                                            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="name">Name (A-Z)</SelectItem>
                                                    <SelectItem value="category">Category</SelectItem>
                                                    <SelectItem value="recent">Most Recent</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">
                                            {filteredDrills.length} drill{filteredDrills.length !== 1 ? 's' : ''} found
                                        </span>
                                        <Button
                                            onClick={() => {
                                                setSearchTerm('');
                                                setSelectedCategory('All');
                                                setSelectedDomain('All');
                                                setSortBy('name');
                                            }}
                                            variant="link"
                                            size="sm"
                                        >
                                            Clear Filters
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Drill Grid */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredDrills.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <p>No drills found matching your criteria.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredDrills.map(drill => (
                                    <div
                                        key={drill.id}
                                        onClick={() => handleSelectDrill(drill.id)}
                                        className={`cursor-pointer transition-all ${selectedDrillId === drill.id ? 'ring-2 ring-primary rounded-md' : 'hover:opacity-80'}`}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSelectDrill(drill.id); }}
                                        aria-pressed={selectedDrillId === drill.id}
                                    >
                                        <DrillCard
                                            drill={drill}
                                            selectionMode={false}
                                            isSelected={false}
                                            onSelectionChange={() => {}}
                                            allDrills={drills}
                                            setGenericModalContent={() => {}}
                                            onUpdateDrill={() => Promise.resolve()}
                                            onDeleteDrill={() => Promise.resolve()}
                                            setActiveFilters={() => {}}
                                            tagPreferences={{}}
                                            activeFilters={{}}
                                            isAdminMode={false}
                                            isFavorite={false}
                                            onToggleFavorite={() => {}}
                                            colorizationCategory=""
                                            layoutMode={'grid' as any}
                                            cardPreferences={{} as any}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-between mt-6 gap-3">
                        <div className="text-sm text-muted-foreground">
                            {selectedDrillId ? 'Selected drill ready to add' : 'Select a drill to enable Add'}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button onClick={onClose} variant="outline">
                                Cancel
                            </Button>
                            <Button
                                onClick={() => { if (selectedDrillId) { onSelect(selectedDrillId); onClose(); } }}
                                disabled={!selectedDrillId}
                            >
                                Add Drill to Slot
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DrillPickerModal;

